import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import SizeHelpers from '../common/size-helpers';

import { usePrintsConfigStore } from './use-prints-config-store';
import { usePrintsOrderStore } from './use-prints-order-store';
import { UpdatePhotoEvents } from '../common/events/event-declarations';
import { VIEW_MODE } from '../constants';
import ViewingSizeKey from '../mobile/viewing-size-key';
import { getSelectedDeliveryOption } from '../utils/delivery-options';
import { getSelectedFinish } from '../utils/finish-options';
import {
  selectSizesWithPhotos,
  selectAvailableSizesForDeliveryAndFinish,
} from '../stores/selectors';

export const usePrintsUiStore = create(
  immer((set, get) => ({
    // --- STATE ---
    ready: false, // is prints builder ready for use
    isSaving: false,
    autoSaveEnabled: true,
    currentlyLoading: undefined, // { index, total }
    viewingSizeKey: SizeHelpers.ALL_PHOTOS_KEY, // Size filter on mobile
    selectedFinishName: null, // currently selected print finish
    selectedDeliveryKey: null, // currently selected delivery option
    selectedUploadSizeKey: null, // size for new uploads, derived from initial options or viewingSizeKey
    currentView: VIEW_MODE.PHOTOS, // currently displayed main pane view
    pendingUiEvent: null, // temporary bridge to components that rely on listening on legacy event system

    // --- ACTIONS ---
    setReady: (isReady) =>
      set((state) => {
        state.ready = isReady;
      }),

    setPhotoView: () =>
      set((state) => {
        state.currentView = VIEW_MODE.PHOTOS;
      }),

    setDuplicateView: () =>
      set((state) => {
        state.currentView = VIEW_MODE.DUPLICATES;
      }),

    toggleDuplicatePhotoView: () =>
      set((state) => {
        state.currentView =
          state.currentView === VIEW_MODE.PHOTOS
            ? VIEW_MODE.DUPLICATES
            : VIEW_MODE.PHOTOS;
      }),

    toggleDebugView: () =>
      set((state) => {
        state.currentView =
          state.currentView === VIEW_MODE.DEBUG
            ? VIEW_MODE.PHOTOS
            : VIEW_MODE.DEBUG;
      }),

    setInitialSelections: ({
      finishName,
      deliveryKey,
      viewingSizeKey,
      initialUploadSizeKey,
    }) =>
      set((state) => {
        state.selectedFinishName = finishName;
        state.selectedDeliveryKey = deliveryKey;
        state.viewingSizeKey = viewingSizeKey;
        state.selectedUploadSizeKey = initialUploadSizeKey;
      }),

    toggleAutoSave: () =>
      set((state) => {
        state.autoSaveEnabled = !state.autoSaveEnabled;
      }),

    setIsSaving: (isSaving) =>
      set((state) => {
        state.isSaving = isSaving;
      }),

    setViewingSizeKey: (sizeKey) =>
      set((state) => {
        state.viewingSizeKey = sizeKey;
      }),

    setSelectedUploadSizeKey: (sizeKey) =>
      set((state) => {
        state.selectedUploadSizeKey = sizeKey;
      }),

    setDeliveryAndFinish: (deliveryOption, finish) => {
      set((state) => {
        state.selectedDeliveryKey = deliveryOption.key;
        state.selectedFinishName = finish.name;
      });
    },

    // Orchestrates the process of updating the user's selected delivery option or print finish.
    // It ensures the UI state is updated first, handles any invalid photo sizes resulting
    // from the change, applies finish-related photo state mutations, and finally triggers a
    // save of all photos to persist the new finish/size and delivery option for the entire order.
    handleDeliveryOptionOrFinishChanged: ({
      finish: chosenFinish,
      delivery_option: chosenDeliveryOption,
    }) => {
      const { setPhotoQuantity, saveAllPhotos, photos } =
        usePrintsOrderStore.getState();
      const { allDeliveryOptions, allSizes, allFinishes } =
        usePrintsConfigStore.getState();
      const {
        setDeliveryAndFinish,
        setPendingUiEvent,
        selectedDeliveryKey,
        selectedFinishName,
      } = get();

      const finish =
        chosenFinish || getSelectedFinish(allFinishes, selectedFinishName);
      const deliveryOption =
        chosenDeliveryOption ||
        getSelectedDeliveryOption(allDeliveryOptions, selectedDeliveryKey);

      const sizesWithPhotos = selectSizesWithPhotos(allSizes, photos).filter(
        (s) => s.photos.length > 0,
      );

      const availableSizesForNewOptions =
        selectAvailableSizesForDeliveryAndFinish(
          allSizes,
          deliveryOption?.key,
          finish?.name,
        );
      const availableSizeKeys = availableSizesForNewOptions.map((s) => s.key);

      const invalidSizes = sizesWithPhotos.filter((size) => {
        const keyWithoutFinish = size.key.split(';', 2).join(';');
        return !availableSizeKeys.some(
          (availableSizeKey) =>
            availableSizeKey.match(keyWithoutFinish) &&
            size.options.delivery_options.includes(deliveryOption.key),
        );
      });

      if (invalidSizes.length) {
        const onContinue = () => {
          // Set quantity of photos in invalid sizes to 0
          invalidSizes.forEach((size) => {
            const rootImageIds = size.photos.map((p) => p.root_image_id);
            setPhotoQuantity({
              rootImageIds,
              targetSizeKey: size.key,
              newQuantity: 0,
            });
          });

          // Set the new delivery and finish options
          setDeliveryAndFinish(deliveryOption, finish);

          const { updatePhotosToNewFinish, photos } =
            usePrintsOrderStore.getState();
          const validSizesWithPrints = selectSizesWithPhotos(
            allSizes,
            photos,
          ).filter(
            (s) =>
              s.photos.length > 0 &&
              !invalidSizes.find((is) => is.key === s.key),
          );

          updatePhotosToNewFinish(finish, validSizesWithPrints);
          saveAllPhotos();
        };

        // Fire event for legacy modal to pick up
        setPendingUiEvent({
          type: UpdatePhotoEvents.SHOW_INVALID_SIZES_MODAL,
          payload: { invalidSizes, onContinue },
        });
      } else {
        const { updatePhotosToNewFinish } = usePrintsOrderStore.getState();

        // Set the new delivery and finish options
        setDeliveryAndFinish(deliveryOption, finish);
        updatePhotosToNewFinish(finish, sizesWithPhotos);
        saveAllPhotos();

        const { viewingSizeKey } = get();
        if (viewingSizeKey !== SizeHelpers.ALL_PHOTOS_KEY) {
          const newViewingSizeKey = new ViewingSizeKey(viewingSizeKey).update(
            'finish',
            finish.name,
          );
          set({ viewingSizeKey: newViewingSizeKey });
        }
      }
    },

    uploadBegun: (total) =>
      set((state) => {
        state.currentlyLoading = { index: 0, total };
      }),

    uploadProgress: () =>
      set((state) => {
        if (!state.currentlyLoading) return;
        const newIndex = state.currentlyLoading.index + 1;
        const isComplete = newIndex === state.currentlyLoading.total;
        state.currentlyLoading = isComplete
          ? null
          : { ...state.currentlyLoading, index: newIndex };

        // Periodic save of order every 10 photos
        if (newIndex % 10 === 0) {
          usePrintsOrderStore.getState().debouncedSave();
        }
      }),

    uploadAborted: () =>
      set((state) => {
        state.currentlyLoading = null;
      }),

    setPendingUiEvent: (params) =>
      set((state) => {
        state.pendingUiEvent = params;
      }),

    clearPendingUiEvent: () => set({ pendingUiEvent: null }),
  })),
);
